import { request } from './client';
import type { SourceId } from './calendar';
import type { ConfidenceLevel } from './suggestions';

export type NoteKind = 'agent' | 'user' | 'edited';

export interface BillingTarget {
  wbs1: string; wbs1Name: string;
  wbs2: string; wbs2Name: string;
  wbs3: string; wbs3Name: string;
  // VP time-category code (ADR-0044). Set only on category targets; the code
  // rides through to SaveTimesheetEntry.category, where VP resolves the alias
  // onto its WBS at save. Absent on ordinary project targets.
  category?: string;
}

/** A target's identity as one string: the WBS path plus the time-category code.
 *
 * The category is part of it. A time category and a project can share a code
 * (ADR-0044), so the WBS path alone identifies two different things, and code
 * that matches on the path sends an edit to whichever the draft lists first.
 * Everything that keys, finds or compares a target goes through here, so that
 * rule holds in one place instead of at each call site. */
export function targetKey(t: Pick<BillingTarget, 'wbs1' | 'wbs2' | 'wbs3' | 'category'>): string {
  return `${t.wbs1}|${t.wbs2}|${t.wbs3}|${t.category ?? ''}`;
}

export function sameTarget(
  a: Pick<BillingTarget, 'wbs1' | 'wbs2' | 'wbs3' | 'category'> | null,
  b: Pick<BillingTarget, 'wbs1' | 'wbs2' | 'wbs3' | 'category'>,
): boolean {
  return !!a && targetKey(a) === targetKey(b);
}

// Hours for one (target, day) cell, with the note describing that day's work
// on the target (ADR-0028 amendment 2026-06-08 — notes are per-(target, day)).
/**
 * `written` marks a cell whose work is already recorded, so triage can show
 * what is outstanding without hiding what is done (#6240).
 *  - 'published' — written from this app, matched by source event id
 *  - 'in-vp'     — the timesheet carries a cell of this shape, whoever wrote it
 */
export type WrittenState = 'published' | 'in-vp';

export interface DayHours { dayIndex: number; hours: number; note: DraftNote; written?: WrittenState; }

// dayIndex is the period-relative day this event's hours land on, so a cell
// (target, day) can resolve its contributing events when the user re-targets it.
export interface ContributingEvent { eventId: string; hours: number; signatureHash: string; dayIndex: number; source?: SourceId; }

// The note carries its own confidence — the note dimension is per-(target, day),
// so it rides here rather than on the per-row RowConfidence.
export interface DraftNote { text: string; kind: NoteKind; confidence: ConfidenceLevel; }

export interface RowConfidence {
  target: ConfidenceLevel;
  hours: ConfidenceLevel;
  billable: ConfidenceLevel;
}

// How the row's target was chosen. Backend emits the three auto mechanisms —
// preserved across reload (ADR-0045 §6), so a reloaded auto-fill still shows
// how it was chosen. 'saved' is the fallback for pre-§6 drafts that stored no
// source; 'manual' is set client-side by reconcileDraft when the user picks the
// target.
export type AttributionSource = 'rule' | 'memory' | 'agent' | 'saved' | 'manual';

export interface DraftRow {
  target: BillingTarget;
  days: DayHours[];
  contributingEvents: ContributingEvent[];
  confidence: RowConfidence;
  reasoning: string;
  // Optional so existing fixtures stay valid; the API always populates it.
  attributionSource?: AttributionSource;
  // VP SubLevel=="Y" — time must charge to a WBS2/WBS3 leaf. Carried from the
  // backend DraftRow (5847); the engine sets it when the matched project is phased.
  isPhased?: boolean;
  // isPhased && wbs2 set && the chosen phase has tasks but no task could be
  // auto-defaulted (all tasks inactive, etc.). The picker enforces task selection
  // when the user opens the leaf editor; the grid shows an amber affordance.
  needsTask?: boolean;
  // Set by reconcileDraft (view-only projection) when the user has confirmed the
  // row's attribution via the overlay — the grid flips the confidence rail to the
  // 'confirmed' state regardless of the engine's original target confidence.
  confirmed?: boolean;
}

// subject/start/hours are display fields for the picker. Optional so existing
// fixtures stay valid; the API always populates them.
export interface NeedsInputItem {
  eventId: string;
  subject?: string;
  start?: string;
  hours?: number;
  candidateTargets: BillingTarget[];
  // Opaque memory key (ADR-0027 §3); the API populates it so a "Pick project"
  // correction records against the right signature at save.
  signatureHash?: string;
  // Set when this app already wrote the event's work, matched by source event
  // id. A held card has no target, so the shape match that covers rows cannot
  // apply — the id is the only signal, and it is the exact one.
  written?: WrittenState;
  // Period-relative day the event falls on (engine-computed in the user's zone),
  // so a pick lands on the right day without client-side timezone math.
  dayIndex?: number;
  // Why the engine held this event — the agent's reasoning for a below-confidence
  // guess, or a generic phrase for an overlap conflict / no-signal item. Shown on
  // the card like a skipped event's reason. Optional so old fixtures stay valid.
  reason?: string;
  // The agent's note carried from the discarded verdict; the resolver pre-fills
  // its comment from it. Empty when no verdict produced one.
  note?: string;
  // Originating source id ('outlook' | 'freshdesk' | …). Re-derived per response,
  // null on a rehydrated saved draft whose event left the read (B4 / C2).
  source?: string;
}

export interface FilteredOutItem { eventId: string; reason: string; }

// 'check' is 'auto' plus a read of every source, for drift (ADR-0031 amendment 2026-09-26).
export type DraftTrigger = 'auto' | 'check' | 'regenerate_unresolved' | 'redraft_all';

export interface DraftSourceStatus {
  id: string;
  status: string;
  contributed: boolean;
  eventCount: number;
}

// Counts of contributing events that have appeared / changed hours / disappeared
// since the saved draft was generated (ADR-0031). Present only when a saved draft
// was loaded; absent on a freshly generated draft.
export interface DraftDrift { new: number; changed: number; removed: number; }

export interface DraftResponse {
  rows: DraftRow[];
  needsInput: NeedsInputItem[];
  filteredOut: FilteredOutItem[];
  periodObservations: string[];
  sources?: DraftSourceStatus[];
  // Null until the user generates this period's draft (ADR-0031).
  generatedAt?: string | null;
  trigger?: DraftTrigger;
  drift?: DraftDrift;
  // The user's billed target ids in picker-relevance order (B.9, #5796); the
  // picker reorders Recent/Other/search so most-billed targets surface first.
  // Opaque WBS paths (encoded by encodeTargetId), never the per-event signature.
  rankedTargetIds?: string[];
  // Hours in a standard working day (ADR-0044). Picking a category off a
  // needs-input item snaps the entry to this, mirroring the engine's rule for
  // agent-confirmed category rows. Absent on old fixtures; defaults to 8.
  standardDayHours?: number;
  // The tenant's VP time categories as pickable targets (ADR-0044) — the whole
  // set, so the picker can offer any category for any event. Each carries the
  // opaque code in `category`. Absent on old fixtures.
  categories?: BillingTarget[];
  // The weekdays VP does not work, as Date.getDay() numbers (0 = Sunday). A
  // Sun–Thu tenant carries [5, 6]. Shades the grid and defaults the repeat
  // picker. Absent on old fixtures; defaults to Sat/Sun.
  nonWorkingDays?: number[];
  // Written marks for the overlay's own entries, keyed by event id (#6544). A
  // manual entry has no row here to carry one — it exists only in the overlay —
  // so reconcileDraft stamps these on the rows it rebuilds from it.
  writtenEvents?: Record<string, WrittenState>;
  // Each draft project's client (VP BillingClientName), for the grid's Firm
  // column. A list, not a map: the API camel-cases dictionary keys, which would
  // rewrite the WBS1. Response-only; empty when VP's project list was unreachable.
  projectClients?: { wbs1: string; clientName: string }[];
}

// Encodes a target's WBS path into the opaque id used by RankedTargetIds and the
// memory store. MUST stay in sync with BillingTarget.EncodeTargetId on the API
// (ASCII Unit Separator join).
const TARGET_ID_SEP = String.fromCharCode(0x1f);
export function encodeTargetId(t: { wbs1: string; wbs2?: string; wbs3?: string }): string {
  return [t.wbs1, t.wbs2 ?? '', t.wbs3 ?? ''].join(TARGET_ID_SEP);
}

// The user's edit overlay entry — outcome-shaped, mirrors the persisted
// SavedDraftEdit (ADR-0031). Structurally identical to the triage store's
// TriageDraft so the store can round-trip it without a mapping layer.
export interface DraftEdit {
  eventId: string;
  status: string;
  wbs1?: string;
  wbs1Name?: string;
  wbs2?: string;
  wbs2Name?: string;
  wbs3?: string;
  wbs3Name?: string;
  hours?: number;
  comment?: string;
  // Provenance of `comment` ('agent' | 'user' | 'edited') — keeps a carried
  // agent note attributed to the agent after a re-target moves the cell.
  commentKind?: NoteKind;
  laborCode?: string;
  dayIndex?: number;
  signatureHash?: string;
  // VP time-category code carried when the user picks a category (ADR-0044), so
  // the pick survives a server round-trip and reaches SaveTimesheetEntry.category.
  category?: string;
  // "Use anyway" on a Duplicate — the user overruling the in-VP shape match,
  // saying the work is genuinely separate. Persisted so the row does not vanish
  // again on reload (#6487).
  useAnyway?: boolean;
}

export function getDraft(from?: string, to?: string, tz?: string, trigger?: DraftTrigger): Promise<DraftResponse> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (tz) params.set('tz', tz);
  // 'auto' is the server default; send any other trigger explicitly.
  if (trigger && trigger !== 'auto') params.set('trigger', trigger);
  const qs = params.toString();
  return request<DraftResponse>(`/api/suggestions/draft${qs ? `?${qs}` : ''}`);
}

// Debounced save of the edit overlay for a period (ADR-0031). 204 on success.
export function saveDraftEdits(from: string, entries: DraftEdit[]): Promise<void> {
  return request<void>('/api/suggestions/draft/edits', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ from, entries }),
  });
}

// Loads the saved edit overlay for a period — empty array when none saved.
export function getDraftEdits(from: string): Promise<DraftEdit[]> {
  return request<DraftEdit[]>(`/api/suggestions/draft/edits?from=${encodeURIComponent(from)}`);
}

// Regenerate one (target, day) cell's note on demand. The server re-collects the
// cell's contributing events from the window and runs a note-only agent call
// (the target is fixed). Nothing is persisted — the caller holds the result.
export interface RegenerateNoteRequest {
  target: BillingTarget;
  eventRefs: string[];
  from?: string;
  to?: string;
  tz?: string;
}

export interface RegenerateNoteResponse { note: DraftNote; }

export function regenerateNote(req: RegenerateNoteRequest): Promise<RegenerateNoteResponse> {
  return request<RegenerateNoteResponse>('/api/suggestions/notes/regenerate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
}
